import * as BABYLON from '@babylonjs/core'
import { DisposerBag } from '../../sceneKit/disposerBag'

export interface VegetationPlacement {
  x: number
  z: number
  scale?: number
  rotY?: number
}

export interface VegetationSpawner {
  /** Safe to call before the model finishes loading — placements queue and flush once it's ready. */
  spawn: (placement: VegetationPlacement) => void
}

/**
 * Loads one real Poly Haven glTF once (same `LoadAssetContainerAsync` +
 * `cancelled` guard pattern as `useStudioLegacyRoom.ts`'s classic-scene
 * loader) and hands back a `spawn()` you can call per scatter position.
 * Each call instances the whole loaded hierarchy — `buildMountainLot` stays
 * synchronous; the model just pops in once the background fetch resolves.
 */
export function createVegetationSpawner(scene: BABYLON.Scene, bag: DisposerBag, rootUrl: string, fileName: string): VegetationSpawner {
  let container: BABYLON.AssetContainer | null = null
  let cancelled = false
  const queued: VegetationPlacement[] = []

  const place = ({ x, z, scale = 1, rotY = 0 }: VegetationPlacement) => {
    if (!container) return
    const entries = container.instantiateModelsToScene((name) => `${fileName}-${name}-${Math.random().toString(36).slice(2, 7)}`, false)
    bag.track({ dispose: () => entries.dispose() })
    const root = entries.rootNodes[0]
    if (!root) return
    root.position.set(x, 0, z)
    root.scaling.setAll(scale)
    root.rotation.y = rotY
    root.getChildMeshes().forEach((mesh) => {
      mesh.isPickable = false
      mesh.receiveShadows = true
    })
  }

  BABYLON.SceneLoader.LoadAssetContainerAsync(rootUrl, fileName, scene)
    .then((loaded) => {
      if (cancelled) {
        loaded.dispose()
        return
      }
      container = loaded
      queued.forEach(place)
      queued.length = 0
    })
    .catch((err) => {
      // eslint-disable-next-line no-console
      console.error('[gallery-studio-3d] mountain lot vegetation failed to load', fileName, err)
    })

  bag.defer(() => {
    cancelled = true
    container?.dispose()
    container = null
  })

  return {
    spawn: (placement) => {
      if (container) place(placement)
      else queued.push(placement)
    },
  }
}
